import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { useSignalR } from "../../../context/SignalRContext";
import ApiCall from "../../../Apicall/ApiCall";
import { confirmWaitOrDisconnect, escapeHtml, showDealExpiredDialog, showSellerCancelledDialog } from "./dealDialogs";
import { fetchDealStatus } from "../../lib/dealStatus";
import { useUser } from "../../../context/UserContext";
import {
  BEFORE_LOGOUT_EVENT,
  DEAL_CANCELLED_EVENT,
  DEAL_HOME_PATH,
  DEAL_LEFT_CANCEL,
  DEAL_LEFT_LEAVE,
  DEAL_COMPLETED_EVENT,
  DEAL_LEFT_LOGOUT,
  DEAL_LEFT_TIMEOUT,
  announceDealEnded,
  buildDealLeftMessage,
  isDealFlowPath,
  parseDealLeftMessage,
  takeIdleLogout,
} from "../../lib/dealSession";

// One id per browser tab, so a tab can ignore the echo of its own SignalR
// message without knowing whether it is the seller or the buyer.
const CLIENT_ID = `c${Math.random().toString(36).slice(2, 10)}`;

// How often a tab may report that its user is actually doing something on the deal. Matches the
// server's own 30s granularity, so a beat is never wasted.
const HEARTBEAT_MS = 30000;
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];

// Which role the OTHER party holds, so a notice can name them. Falls back to "buyer", which
// makes the notice read a little generically rather than not appear at all.
async function counterpartRoleOf(dealId, userId) {
  if (!dealId || !userId) return "buyer";
  try {
    const res = await ApiCall({ url: "/ContractMain/GetContractMainById", method: "GET", params: { Id: dealId } });
    const deal = res?.data?.result || res?.result;
    if (!deal) return "buyer";
    return String(deal.sellerUserId) === String(userId) ? "buyer" : "seller";
  } catch (_) {
    return "buyer";
  }
}

const hasSession = () => {
  try {
    return Boolean(localStorage.getItem("authToken"));
  } catch (_) {
    return false;
  }
};

// Mounted once (app.jsx), always alive — unlike the deal pages it does not
// unmount when the user navigates, so it can own the rules that must hold
// everywhere while two parties are connected to a deal:
//  1. Signing out (manual or idle timeout) tells the counterpart, who is sent
//     to the home page. The deal stays active until its 60-minute expiry.
//  2. Leaving the deal flow for any other page asks "Leave the deal?" first.
//     Confirming sends both parties home with the deal still active.
export default function DealSessionManager() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { dealId, connection, messages, sendDealMessage, leaveDealGroup, connectToDeal } = useSignalR();
  // Only to work out which side the counterpart is on, for the notices that name them.
  const { userId } = useUser();
  const myUserId = useRef(userId);
  myUserId.current = userId;

  // Everything the long-lived listeners need, read through a ref so they never
  // go stale (and never have to be re-registered on every render).
  const live = useRef({});
  live.current = { t, navigate, pathname, dealId, connection, sendDealMessage, leaveDealGroup, connectToDeal };

  const leaving = useRef(false);
  // Is the user inside a deal they should be asked about leaving? A live deal (connected), or the
  // page of a sale that has just been completed: that one has no live connection any more, but
  // leaving its page is still confirmed - the answer only takes the user home, nobody is told.
  const finishedHere = (path) =>
    finishedDeal.current != null &&
    isDealFlowPath(path || "") &&
    String(path).split("/").filter(Boolean).pop() === String(finishedDeal.current);
  const guarded = (path) => Boolean(live.current.dealId) || finishedHere(path);
  // The deal that has just been completed on this tab. Its connection is dropped (below) and any
  // "left the deal" message still in flight for it is ignored.
  const finishedDeal = useRef(null);
  const handled = useRef(new WeakSet());
  const lastFlowPath = useRef(null);
  const dealPopupOpen = useRef(false);

  // Tell the counterpart and drop the SignalR group (the deal keeps its fixed
  // 60-minute-from-creation expiry; nothing is extended). Safe to call more than once.
  const announceLeft = useCallback(
    async (reason) => {
      const { dealId: id, connection: conn, sendDealMessage: send, leaveDealGroup: leave } = live.current;
      if (!id || !conn) return;
      try {
        await send("deal", buildDealLeftMessage(reason, CLIENT_ID));
      } catch (_) {}
      try {
        await leave();
      } catch (_) {}
    },
    []
  );

  // (1a) This user signs out — manually or via SessionTimeoutManager. Both go
  // through logout(), which waits for the tasks pushed here.
  useEffect(() => {
    const onBeforeLogout = (e) => {
      if (!live.current.dealId) return;
      e.detail?.tasks?.push(announceLeft(takeIdleLogout() ? DEAL_LEFT_TIMEOUT : DEAL_LEFT_LOGOUT));
    };
    window.addEventListener(BEFORE_LOGOUT_EVENT, onBeforeLogout);
    return () => window.removeEventListener(BEFORE_LOGOUT_EVENT, onBeforeLogout);
  }, [announceLeft]);

  // Once this user is signed out (manually or by the idle timeout) no deal pop-up may
  // linger over the landing page, so close any open dialog as soon as the token is gone.
  useEffect(() => {
    const id = setInterval(() => {
      if (dealPopupOpen.current && !hasSession() && Swal.isVisible()) Swal.close();
    }, 300);
    return () => clearInterval(id);
  }, []);

  // A live deal started later supersedes the finished one.
  useEffect(() => {
    if (dealId) finishedDeal.current = null;
  }, [dealId]);

  // (1a') The sale is complete (the seller reported the transfer). The deal is finished, so the
  // connection to the other party is closed on both sides: no "Leave the deal?" question, no
  // "left the deal" notice, no counterpart messages after this point.
  useEffect(() => {
    const onCompleted = async (e) => {
      const id = e.detail?.dealId;
      const { dealId: current, leaveDealGroup: leave } = live.current;
      if (!current || (id && String(id) !== String(current))) return;
      finishedDeal.current = current;
      try {
        await leave();
      } catch (_) {}
    };
    window.addEventListener(DEAL_COMPLETED_EVENT, onCompleted);
    return () => window.removeEventListener(DEAL_COMPLETED_EVENT, onCompleted);
  }, []);

  // (1b / 2b) The OTHER party signed out or left: tell this user, then home.
  useEffect(() => {
    if (!messages?.length) return;
    for (const m of messages) {
      if (!m || handled.current.has(m)) continue;
      const left = parseDealLeftMessage(m.message);
      if (!left) continue;
      handled.current.add(m);
      if (left.clientId === CLIENT_ID || leaving.current) continue;
      // A completed deal is over: nothing about it is announced any more.
      if (finishedDeal.current && String(finishedDeal.current) === String(live.current.dealId)) continue;

      const loggedOut = left.reason === DEAL_LEFT_LOGOUT;
      const timedOut = left.reason === DEAL_LEFT_TIMEOUT;
      const cancelled = left.reason === DEAL_LEFT_CANCEL;
      const endedDealId = live.current.dealId;
      leaving.current = true;
      (async () => {
        const { t: tr, navigate: nav, leaveDealGroup: leave } = live.current;
        try {
          await leave();
        } catch (_) {}
        // This user may already be signed out (manually or by the idle timeout).
        if (!hasSession()) {
          leaving.current = false;
          return;
        }
        dealPopupOpen.current = true;
        if (cancelled) {
          // The notice names the vehicle and both parties, so read the deal it is about.
          // Best-effort: if this fails the buyer still gets the message, just without the facts.
          let cancelledDeal = {};
          if (endedDealId) {
            try {
              const res = await ApiCall({
                url: "/ContractMain/GetContractMainById",
                method: "GET",
                params: { Id: endedDealId },
              });
              cancelledDeal = res?.data?.result || res?.result || {};
            } catch (_) {}
          }
          // Either party may cancel now, so the notice names whoever did: if this user is the
          // seller of the deal, it was the buyer who cancelled it.
          const cancelledBy =
            cancelledDeal?.sellerUserId && String(cancelledDeal.sellerUserId) === String(myUserId.current)
              ? "buyer"
              : "seller";
          await showSellerCancelledDialog(tr, cancelledDeal, cancelledBy);
        } else if (timedOut) {
          // The counterpart's session ended on the idle timeout. The deal itself stays open until
          // its own inactivity expiry, so this user gets the choice the flow was always meant to
          // offer - wait for them to come back, or disconnect - instead of only being told.
          const counterpartRoleKey = await counterpartRoleOf(endedDealId, myUserId.current);
          const disconnect = await confirmWaitOrDisconnect(tr, counterpartRoleKey);
          if (!disconnect) {
            // "Wait": stay put and rejoin the deal's group, so the counterpart signing back in
            // (or the deal finally expiring) still reaches this tab.
            dealPopupOpen.current = false;
            try {
              await live.current.connectToDeal(endedDealId);
            } catch (_) {}
            leaving.current = false;
            return;
          }
        } else {
          await Swal.fire({
            icon: "info",
            title: escapeHtml(tr(loggedOut ? "dealSession.otherLoggedOutTitle" : "dealSession.otherLeftTitle")),
            text: tr(loggedOut ? "dealSession.otherLoggedOutBody" : "dealSession.otherLeftBody"),
            confirmButtonText: tr("dealSession.ok"),
            allowOutsideClick: false,
          });
        }
        dealPopupOpen.current = false;
        // A cancelled deal is over: this user has nothing left to resume.
        if (cancelled) {
          try {
            localStorage.removeItem("currentContractID");
            localStorage.removeItem("data");
          } catch (_) {}
          // told live: the login-time notice must not repeat it
          if (endedDealId) {
            try {
              await ApiCall({ url: "/ContractMain/AcknowledgeExpiredDeal", method: "POST", data: { id: endedDealId } });
            } catch (_) {}
          }
        }
        // Closed because this user signed out meanwhile: stay on the landing page.
        if (hasSession()) nav(DEAL_HOME_PATH, { replace: true });
        if (cancelled) announceDealEnded();
        leaving.current = false;
      })();
    }
  }, [messages]);

  // (1c) This user (the seller) cancelled the sale — on the QR step or on the transaction record.
  // Tell the buyer, if one is connected, then drop this tab's deal connection so the "Leave the
  // deal?" guard no longer applies to a deal that is over. From the transaction record this tab
  // has no connection yet, so it joins the deal's group just long enough to send the notice.
  useEffect(() => {
    const onCancelled = (e) => {
      const id = e.detail?.dealId;
      const tasks = e.detail?.tasks;
      if (!id || !tasks) return;
      tasks.push(
        (async () => {
          const { dealId: current, connectToDeal: join, leaveDealGroup: leave } = live.current;
          try {
            if (current && String(current) === String(id)) {
              await announceLeft(DEAL_LEFT_CANCEL);
            } else {
              if (current) await leave();
              await join(id, { user: "deal", message: buildDealLeftMessage(DEAL_LEFT_CANCEL, CLIENT_ID) });
              await leave();
            }
          } catch (_) {
            /* the buyer sees the cancelled deal on their next load */
          }
        })()
      );
    };
    window.addEventListener(DEAL_CANCELLED_EVENT, onCancelled);
    return () => window.removeEventListener(DEAL_CANCELLED_EVENT, onCancelled);
  }, [announceLeft]);

  // (1d) A buyer who was connected to a deal the seller has since cancelled, but was not in the deal
  // when it happened (they had been sent home), is told once while signed in — the same notice
  // they would otherwise get at their next login. Expired deals keep their login-time notice.
  useEffect(() => {
    let stopped = false;
    const check = async () => {
      if (stopped || leaving.current || dealPopupOpen.current || live.current.dealId || !hasSession() || Swal.isVisible()) return;
      try {
        const res = await ApiCall({ url: "/ContractMain/GetExpiredDealNotice", method: "GET" });
        const deal = res?.result || res?.data?.result;
        if (stopped || !deal?.id || deal.dealState !== "cancelled" || !hasSession() || live.current.dealId) return;
        dealPopupOpen.current = true;
        // Either party may cancel, so the notice follows the server's record of who did rather
        // than assuming the seller.
        await showSellerCancelledDialog(live.current.t, deal, deal.cancelledByRole === "buyer" ? "buyer" : "seller");
        dealPopupOpen.current = false;
        try {
          localStorage.removeItem("currentContractID");
          localStorage.removeItem("data");
        } catch (_) {}
        try {
          await ApiCall({ url: "/ContractMain/AcknowledgeExpiredDeal", method: "POST", data: { id: deal.id } });
        } catch (_) {}
        if (hasSession() && isDealFlowPath(live.current.pathname)) live.current.navigate(DEAL_HOME_PATH, { replace: true });
        announceDealEnded();
      } catch (_) {
        /* transient: try again on the next tick */
      }
    };
    const id = setInterval(check, 15000);
    const first = setTimeout(check, 3000);
    return () => {
      stopped = true;
      clearInterval(id);
      clearTimeout(first);
    };
  }, []);

  // (3) The 60-minute deal lifetime runs out while this user is signed in and connected:
  // say so (with a "go to home page" button), mark the notice as seen so it is not repeated
  // on the next login, and go home. A user who is signed out at that moment sees nothing now —
  // the same notice is shown once when they next sign in (showExpiredDealNoticeIfAny).
  useEffect(() => {
    if (!dealId) return undefined;
    let stopped = false;
    const check = async () => {
      if (stopped || leaving.current || !hasSession()) return;
      try {
        // Read-only on purpose: watching for expiry must not keep the deal alive. Reading the
        // contract itself records activity, which is why an open tab used to be immortal - the
        // heartbeat below is what reports that the user is really there.
        const status = await fetchDealStatus(dealId);
        if (stopped || status?.dealState !== "expired" || !hasSession()) return;

        // Only now, for the notice itself, read the facts it names.
        let deal = { id: dealId };
        try {
          const res = await ApiCall({ url: "/ContractMain/GetContractMainById", method: "GET", params: { Id: dealId } });
          deal = res?.result || res?.data?.result || deal;
        } catch (_) {
          /* the notice then shows without the vehicle-and-parties block */
        }

        stopped = true;
        leaving.current = true;
        const { t: tr, navigate: nav, leaveDealGroup: leave } = live.current;
        try {
          await leave();
        } catch (_) {}
        dealPopupOpen.current = true;
        await showDealExpiredDialog(tr, deal);
        dealPopupOpen.current = false;
        if (hasSession()) {
          localStorage.removeItem("currentContractID");
          localStorage.removeItem("data");
          try {
            await ApiCall({ url: "/ContractMain/AcknowledgeExpiredDeal", method: "POST", data: { id: deal.id } });
          } catch (_) {}
          nav(DEAL_HOME_PATH, { replace: true });
          announceDealEnded();
        }
        leaving.current = false;
      } catch (_) {
        /* transient error: the next tick tries again */
      }
    };
    const id = setInterval(check, 10000);
    check();
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [dealId]);

  // (4) Report that this user is genuinely doing something on the deal. This is now the only
  // thing that extends it, which is what the 60-minute inactivity rule is supposed to mean: the
  // watcher above reads without touching, so a tab left open on its own no longer keeps a deal
  // alive for ever. Sent at most every HEARTBEAT_MS, and only when there has been real activity.
  useEffect(() => {
    if (!dealId) return undefined;
    let active = false;
    const mark = () => {
      active = true;
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    const beat = async () => {
      if (!active || leaving.current || !hasSession()) return;
      active = false;
      try {
        await ApiCall({ url: "/ContractMain/Heartbeat", method: "POST", data: { id: Number(dealId) } });
      } catch (_) {
        /* best effort: the next beat tries again */
      }
    };
    const id = setInterval(beat, HEARTBEAT_MS);
    return () => {
      clearInterval(id);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, mark));
    };
  }, [dealId]);

  // This user chose to leave (confirmed): notify, then both go home.
  const leaveDealNow = useCallback(async () => {
    if (leaving.current) return;
    leaving.current = true;
    await announceLeft(DEAL_LEFT_LEAVE);
    finishedDeal.current = null;
    live.current.navigate(DEAL_HOME_PATH, { replace: true });
    leaving.current = false;
  }, [announceLeft]);

  // This user chose to cancel the deal. Either party may, while the deal is still running, and the
  // effect is the seller's own cancel from the transaction history: the server closes the deal,
  // the counterpart is told over SignalR, and both end up on the home page.
  const cancelDealNow = useCallback(async () => {
    if (leaving.current) return;
    const { dealId: id, t: tr, navigate: nav } = live.current;
    if (!id) return;
    leaving.current = true;
    try {
      await ApiCall({ url: "/ContractMain/DealCanceledByParty", method: "POST", data: { id: Number(id) } });
    } catch (err) {
      // Refused (already signed, already expired) or unreachable: say why and stay put, rather
      // than navigating away from a deal that is still live.
      leaving.current = false;
      const backend = err?.response?.data?.error;
      await Swal.fire({
        icon: "error",
        title: backend?.message || tr("dealSession.cancelFailedTitle"),
        text: backend?.details || tr("dealSession.cancelFailedBody"),
      });
      return;
    }
    await announceLeft(DEAL_LEFT_CANCEL);
    try {
      localStorage.removeItem("currentContractID");
      localStorage.removeItem("data");
    } catch (_) {}
    nav(DEAL_HOME_PATH, { replace: true });
    announceDealEnded();
    leaving.current = false;
  }, [announceLeft]);

  // Leaving the deal flow: stay, leave the deal running, or cancel it outright.
  // Resolves "stay" | "leave" | "cancel"; dismissing the dialog counts as staying.
  const confirmLeave = useCallback(async () => {
    const tr = live.current.t;

    // Cancelling is only offered while there is something to cancel: a signed or already expired
    // deal is refused by the server, so the button is not shown for one. Read-only, so asking
    // does not extend the deal.
    let canCancel = false;
    // A completed sale has no live deal left: it can only be left, and nothing is cancelled.
    const finished = !live.current.dealId;
    try {
      if (finished) throw new Error("finished");
      const status = await fetchDealStatus(live.current.dealId);
      // A signed deal (payment and ownership stages) can only be left: the server refuses to
      // cancel it, so the choice is not offered.
      canCancel = status?.dealState === "in_progress" && !status?.dealComplete;
    } catch (_) {
      /* unknown: leave the destructive choice out */
    }

    let choice = "stay";
    await Swal.fire({
      html: `
        <div class="ds-swal-price ds-swal-price-confirm">
          <p class="ds-swal-price-title">${escapeHtml(tr(finished ? "dealSession.leaveDoneTitle" : "dealSession.leaveTitle"))}</p>
          <p class="ds-swal-price-desc">${escapeHtml(tr(finished ? "dealSession.leaveDoneBody" : "dealSession.leaveBody"))}</p>
          ${canCancel ? `<p class="ds-swal-price-subtext">${escapeHtml(tr("dealSession.cancelHint"))}</p>` : ""}
          <div class="ds-swal-price-actions">
            <button type="button" class="ds-swal-price-cancel" id="ds-swal-dsm-stay">${escapeHtml(tr("dealSession.leaveCancel"))}</button>
            <button type="button" class="ds-swal-price-disconnect" id="ds-swal-dsm-leave">${escapeHtml(tr("dealSession.leaveConfirm"))}</button>
            ${canCancel ? `<button type="button" class="ds-swal-price-disconnect" id="ds-swal-dsm-cancel">${escapeHtml(tr("dealSession.leaveCancelDeal"))}</button>` : ""}
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: false,
      buttonsStyling: false,
      customClass: { popup: "ds-swal-popup", htmlContainer: "ds-swal-price-container" },
      didOpen: (popup) => {
        popup.querySelector("#ds-swal-dsm-stay").addEventListener("click", () => {
          choice = "stay";
          Swal.close();
        });
        popup.querySelector("#ds-swal-dsm-leave").addEventListener("click", () => {
          choice = "leave";
          Swal.close();
        });
        const cancelBtn = popup.querySelector("#ds-swal-dsm-cancel");
        if (cancelBtn) {
          cancelBtn.addEventListener("click", () => {
            choice = "cancel";
            Swal.close();
          });
        }
      },
    });
    return choice;
  }, []);

  // (2a) Navigating out of the deal flow while connected.
  // Link clicks (header, logo, menu): intercepted before React Router sees them.
  useEffect(() => {
    const onClick = (e) => {
      const { pathname: here } = live.current;
      if (!guarded(here) || leaving.current || e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (!isDealFlowPath(here)) return;
      const a = e.target?.closest?.("a[href]");
      if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
      let url;
      try {
        url = new URL(a.href, window.location.href);
      } catch (_) {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (isDealFlowPath(url.pathname)) return;
      e.preventDefault();
      e.stopPropagation();
      confirmLeave().then((choice) => {
        if (choice === "leave") leaveDealNow();
        else if (choice === "cancel") cancelDealNow();
      });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [confirmLeave, leaveDealNow, cancelDealNow]);

  // Browser back/forward off the deal flow: the router has already moved, so
  // ask, and either finish leaving or put the user back where they were.
  useEffect(() => {
    // Off the deal pages there is nothing to go back to, so Back from the overview (or any other
    // page) is never questioned, even though the deal may still be connected there.
    lastFlowPath.current = isDealFlowPath(pathname) ? pathname : null;
  }, [pathname]);
  useEffect(() => {
    const onPop = () => {
      const here = window.location.pathname;
      const from = lastFlowPath.current;
      if (!(live.current.dealId || finishedHere(from)) || leaving.current || isDealFlowPath(here) || !from) return;
      confirmLeave().then((choice) => {
        if (choice === "leave") leaveDealNow();
        else if (choice === "cancel") cancelDealNow();
        else live.current.navigate(from, { replace: true });
      });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [confirmLeave, leaveDealNow, cancelDealNow]);

  // Closing/reloading the tab: only the browser's native prompt is possible.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!live.current.dealId || !isDealFlowPath(live.current.pathname)) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // Pages with their own "back"/"overview" buttons call this instead of
  // navigate() so those also get the confirmation (see useGuardedNavigate).
  useEffect(() => {
    const onRequest = (e) => {
      const to = e.detail?.to;
      if (!to) return;
      const { navigate: nav } = live.current;
      // Only a deal page asks. The overview (and every other page) stays free even while the deal is
      // still connected there, so the profile, history and terms open without a question.
      if (!isDealFlowPath(live.current.pathname) || !guarded(live.current.pathname) || isDealFlowPath(to)) {
        nav(to);
        return;
      }
      confirmLeave().then((choice) => {
        if (choice === "leave") leaveDealNow();
        else if (choice === "cancel") cancelDealNow();
      });
    };
    window.addEventListener("klargo:guarded-navigate", onRequest);
    return () => window.removeEventListener("klargo:guarded-navigate", onRequest);
  }, [confirmLeave, leaveDealNow, cancelDealNow]);

  return null;
}

// Drop-in replacement for navigate(to) on in-deal buttons that leave the flow.
export function guardedNavigate(to) {
  window.dispatchEvent(new CustomEvent("klargo:guarded-navigate", { detail: { to } }));
}
